// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { MathUtils } from '@shared/utils/MathUtils';
import { Intervals } from '@shared/utils/Instruments/Intervals';
import CreateAlertValueSelectorTemplate from '@front/templates/CreateAlertValueSelectorTemplate.html';
import { ContainerControl } from '@front/controls/elements/ContainerControl';
import { TerceraNumeric } from '@front/controls/elements/TerceraNumeric';
import { AlertType } from '@shared/utils/Alert/AlertConstants';
import { WDSettingsUtils } from '@front/controls/UtilsClasses/WDGeneralSettingsUtils';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
export class CreateAlertValueSelector extends ContainerControl {
    constructor() {
        super();
    }
    getType() {
        return 'CreateAlertValueSelector';
    }
    oninit() {
        super.oninit();
        this.observe('instrument selectedItem', this.onInstrumentChanged);
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
        this.localize();
    }
    onInstrumentChanged(n, o) {
        const instrument = this.get('instrument');
        if (isNullOrUndefined(instrument)) {
            return;
        }
        const Level1 = instrument.Level1;
        if (isNullOrUndefined(Level1)) {
            return;
        }
        const selectedItem = this.get('selectedItem');
        const account = this.get('account');
        const settings = NumericUtils.getPriceNumericParameters(instrument);
        let value = null;
        let allowMinus = true;
        let minValue = settings.step;
        let isTradingNumeric = true;
        let step = settings.step;
        let decimalPrecision = settings.precision;
        let intervals = Intervals.GetIntervalsFromInstrument(instrument, TerceraNumeric.NUMERIC_MAXVALUE);
        if (isValidArray(intervals)) {
            minValue = intervals[0].Increment;
        }
        if (AlertType.ASK === selectedItem.value) {
            value = Level1.GetAsk(account);
            if (isNaN(value)) {
                value = instrument.PointSize;
            }
            minValue = -TerceraNumeric.NUMERIC_MAXVALUE;
        }
        if (AlertType.BID === selectedItem.value) {
            value = Level1.GetBid(account);
            if (isNaN(value)) {
                value = instrument.PointSize;
            }
            minValue = -TerceraNumeric.NUMERIC_MAXVALUE;
        }
        if (AlertType.LAST === selectedItem.value) {
            value = Level1.GetLastPrice(account);
            if (isNaN(value)) {
                value = instrument.PointSize;
            }
            minValue = -TerceraNumeric.NUMERIC_MAXVALUE;
        }
        if (AlertType.CHANGE === selectedItem.value) {
            value = Level1.GetChangePercent(account);
            if (!isValidNumber(value)) {
                value = 0.01;
            }
            step = 0.01;
            decimalPrecision = 2;
            minValue = -TerceraNumeric.NUMERIC_MAXVALUE;
            intervals = null;
            isTradingNumeric = false;
        }
        if (AlertType.VOLUME === selectedItem.value) {
            value = instrument.TotalVolume;
            const inLots = WDSettingsUtils.displayAmountInLots();
            const qtyNumericSettings = Quantity.getQuantityNumericSettings(instrument, account, inLots);
            step = qtyNumericSettings.step; // instrument.LotStep
            minValue = qtyNumericSettings.step; // minValue = 0;
            decimalPrecision = MathUtils.getPrecision(step);
            intervals = null;
            allowMinus = false;
            if (isNaN(value)) {
                value = minValue;
            }
        }
        void this.set({
            numericValue: value,
            step,
            decimalPrecision,
            allowMinus,
            minValue,
            intervals,
            isTradingNumeric
        });
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        super.dispose();
    }
    localize() {
        void this.set('label', Resources.getResource('screen.Alerts.Label.Value'));
    }
}
ContainerControl.extendWith(CreateAlertValueSelector, {
    template: CreateAlertValueSelectorTemplate,
    data: function () {
        return {
            label: '',
            instrument: null,
            width: 200,
            numericValue: 0.01,
            minValue: 0,
            maxValue: TerceraNumeric.NUMERIC_MAXVALUE,
            step: 0.01,
            decimalPrecision: 2,
            allowMinus: false
        };
    }
});
